import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImageIcon, X, Camera } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { PhotoUploader } from './PhotoUploader';

/**
 * PhotoGallery — reusable photo gallery with optional label sections.
 *
 * Props:
 *   photos: [{url, label, caption, created_at}]
 *   onSave(photos: array)
 *   kind: string — GridFS tag (e.g. 'property_photo' | 'renovation_photo')
 *   title: string
 *   showBeforeAfter: bool — enable the label picker + split sections
 *   labels: [{value, display}] — custom label set (defaults to
 *       before/after/general when showBeforeAfter is true)
 *   trigger: JSX
 */
export const PhotoGallery = ({
  photos = [],
  onSave,
  kind = 'generic',
  title = 'Photo Gallery',
  showBeforeAfter = false,
  labels,
  trigger,
  testIdPrefix = 'gallery',
}) => {
  const labelSet =
    labels ||
    (showBeforeAfter
      ? [
          { value: 'before', display: 'Before' },
          { value: 'after', display: 'After' },
          { value: '', display: 'General' },
        ]
      : [{ value: '', display: 'General' }]);
  const [open, setOpen] = useState(false);
  const [nextLabel, setNextLabel] = useState(labelSet[0]?.value ?? '');
  const [nextCaption, setNextCaption] = useState('');
  const [viewingUrl, setViewingUrl] = useState(null);

  const knownVals = new Set(labelSet.map((l) => l.value));

  const grouped = labelSet.map((l) => ({
    ...l,
    items: photos.filter((p) => (p.label || '') === l.value),
  }));
  const orphans = photos.filter((p) => !knownVals.has(p.label || ''));

  const handleAdd = async (url) => {
    const newPhoto = {
      url,
      label: nextLabel,
      caption: nextCaption.trim(),
      created_at: new Date().toISOString(),
    };
    const next = [...photos, newPhoto];
    await onSave(next);
    setNextCaption('');
  };

  const handleRemove = async (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    await onSave(next);
  };

  const renderSection = (key, label, items, emptyText) => (
    <div key={key} className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-slate-500">
        {label} <span className="text-slate-400 normal-case">({items.length})</span>
      </Label>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">{emptyText}</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {items.map((p) => {
            const idx = photos.indexOf(p);
            return (
              <div
                key={`${p.url.slice(-16)}-${idx}`}
                className="relative group rounded-lg overflow-hidden border border-slate-200"
                data-testid={`${testIdPrefix}-photo-${idx}`}
              >
                <img
                  src={p.url}
                  alt={p.caption || label}
                  className="w-full h-24 object-cover cursor-pointer"
                  onClick={() => setViewingUrl(p.url)}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/100?text=!';
                  }}
                />
                {p.caption && (
                  <p className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">
                    {p.caption}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                  aria-label="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid={`${testIdPrefix}-open-btn`}
        >
          <ImageIcon className="h-3.5 w-3.5 mr-1" />
          Photos ({photos.length})
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto"
          data-testid={`${testIdPrefix}-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Camera className="h-5 w-5 text-slate-700" />
              {title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {labelSet.length > 1 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-xs uppercase text-slate-500">Add photo as:</Label>
                {labelSet.map((l) => (
                  <button
                    key={l.value || 'general'}
                    type="button"
                    onClick={() => setNextLabel(l.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      nextLabel === l.value
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`${testIdPrefix}-label-${l.value || 'general'}`}
                  >
                    {l.display}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Caption (optional)</Label>
              <Input
                value={nextCaption}
                onChange={(e) => setNextCaption(e.target.value)}
                placeholder="Kitchen after countertop install"
                data-testid={`${testIdPrefix}-caption-input`}
              />
            </div>

            <PhotoUploader
              kind={kind}
              onAdd={handleAdd}
              testIdPrefix={`${testIdPrefix}-uploader`}
            />

            {labelSet.length > 1 ? (
              <div className="space-y-4 border-t border-slate-100 pt-4">
                {grouped.map((g) =>
                  renderSection(g.value || 'general', g.display, g.items, `No "${g.display}" photos yet.`)
                )}
                {orphans.length > 0 && renderSection('__orphans__', 'Other', orphans, '')}
              </div>
            ) : (
              <div className="border-t border-slate-100 pt-4">
                {renderSection('all', 'Photos', photos, 'No photos yet. Upload one above.')}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingUrl} onOpenChange={() => setViewingUrl(null)}>
        <DialogContent className="sm:max-w-[800px] p-2">
          {viewingUrl && (
            <img src={viewingUrl} alt="Preview" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhotoGallery;
